import React, { useState, useEffect, useCallback, useId, useMemo } from 'react';
import { X, UserPlus, Trash2, Mail, Eye, Edit2, Crown, Clock, Loader2, Check, AlertCircle, FolderKanban } from 'lucide-react';
import {
  shareProject, unshareProject, updateSharePermission, listSharedUsers,
  shareLibraryProject, unshareLibraryProject, updateLibraryProjectPermission, listLibraryProjectMembers,
} from '../utils/storage';
import { SharePermission } from '../types';

interface ShareModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** The workspace's id, or the library project's (kind 'project') */
  projectId: string;
  projectName: string;
  currentUserId: string;
  onEnsureSaved?: () => Promise<void>; // Called before sharing to ensure project is in Firestore
  /** A workspace (the default), or a library project and everything in it */
  kind?: 'workspace' | 'project';
  /** After someone was added, changed or taken off */
  onChanged?: () => void;
}

/** The sharing calls for a workspace, and for a library project (netlify/functions/api/project-sharing.ts) */
const sharingCalls = (kind: 'workspace' | 'project') => (kind === 'project'
  ? { share: shareLibraryProject, remove: unshareLibraryProject, update: updateLibraryProjectPermission, list: listLibraryProjectMembers }
  : { share: shareProject, remove: unshareProject, update: updateSharePermission, list: listSharedUsers });

interface SharedUser {
  uid: string;
  email: string;
  name: string;
  permission: SharePermission;
  sharedAt: number;
  /** Has the workspace through a library project of the owner's (managed in that project's sharing) */
  viaProject?: string;
  viaProjectName?: string;
  /** Also shared on the workspace itself, with this permission */
  directPermission?: SharePermission;
}

interface PendingInvitation {
  id: string;
  email: string;
  /** Someone already on the app, who has to accept it */
  inviteeUid?: string;
  permission: SharePermission;
  createdAt: number;
  inviterName: string;
}

type FeedbackType = 'success' | 'error' | 'info';

export const ShareModal: React.FC<ShareModalProps> = ({
  isOpen,
  onClose,
  projectId,
  projectName,
  currentUserId,
  onEnsureSaved,
  kind = 'workspace',
  onChanged,
}) => {
  const api = useMemo(() => sharingCalls(kind), [kind]);
  const noun = kind === 'project' ? 'project' : 'workspace';
  const [email, setEmail] = useState('');
  const [permission, setPermission] = useState<SharePermission>('view');
  const [sharedUsers, setSharedUsers] = useState<SharedUser[]>([]);
  const [pendingInvitations, setPendingInvitations] = useState<PendingInvitation[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSharing, setIsSharing] = useState(false);
  const [feedback, setFeedback] = useState<{ message: string; type: FeedbackType } | null>(null);
  const titleId = useId();

  const loadSharedUsers = useCallback(async () => {
    setIsLoading(true);
    try {
      const result = await api.list(projectId);
      // A list that could not be read must not look like nobody has access
      if (result.error) setFeedback({ message: `Could not load who has access: ${result.error}`, type: 'error' });
      if (result.users) setSharedUsers(result.users);
      if (result.pendingInvitations) setPendingInvitations(result.pendingInvitations as PendingInvitation[]);
    } catch {
      setFeedback({ message: 'Failed to load shared users', type: 'error' });
    } finally {
      setIsLoading(false);
    }
  }, [projectId, api]);

  useEffect(() => {
    if (isOpen) {
      // Ensure the project is saved to Firestore before loading shared users
      if (onEnsureSaved) {
        onEnsureSaved().then(() => loadSharedUsers()).catch(() => loadSharedUsers());
      } else {
        loadSharedUsers();
      }
      setFeedback(null);
      setEmail('');
    }
  }, [isOpen, loadSharedUsers, onEnsureSaved]);

  const showFeedback = (message: string, type: FeedbackType) => {
    setFeedback({ message, type });
    setTimeout(() => setFeedback(null), 4000);
  };

  const handleShare = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;

    setIsSharing(true);
    try {
      // Ensure project is saved to Firestore before sharing
      if (onEnsureSaved) {
        try { await onEnsureSaved(); } catch (saveErr) {
          console.warn('[ShareModal] Pre-share save failed:', saveErr);
        }
      }
      const result: any = await api.share(projectId, email.trim(), permission);
      if (result.success) {
        // A project asks first: the server says what happens next
        const msg = kind === 'project' && result.message
          ? result.message
          : result.type === 'invitation'
            ? `Invitation sent to ${email.trim()}`
            : `${kind === 'project' ? 'Project' : 'Workspace'} shared with ${email.trim()}`;
        showFeedback(msg, 'success');
        setEmail('');
        loadSharedUsers();
        onChanged?.();
      } else {
        // Show server error details including which step failed
        const errMsg = result.failedAt
          ? `Failed at: ${result.failedAt} — ${result.message || result.error}`
          : (result.error || 'Failed to share');
        showFeedback(errMsg, 'error');
      }
    } catch {
      showFeedback(`Failed to share ${noun}`, 'error');
    } finally {
      setIsSharing(false);
    }
  };

  const handleRevoke = async (userId: string) => {
    try {
      const result = await api.remove(projectId, { userId });
      if (result.success) {
        showFeedback('Access revoked', 'info');
        loadSharedUsers();
        onChanged?.();
      } else {
        showFeedback(result.error || 'Failed to revoke', 'error');
      }
    } catch {
      showFeedback('Failed to revoke access', 'error');
    }
  };

  const handleCancelInvitation = async (invitationId: string) => {
    try {
      const result = await api.remove(projectId, { invitationId });
      if (result.success) {
        showFeedback('Invitation cancelled', 'info');
        loadSharedUsers();
      } else {
        showFeedback(result.error || 'Failed to cancel', 'error');
      }
    } catch {
      showFeedback('Failed to cancel invitation', 'error');
    }
  };

  const handlePermissionChange = async (userId: string, newPermission: SharePermission) => {
    const result = await api.update(projectId, userId, newPermission);
    if (result.success) {
      // On a workspace someone also has through its project, only the workspace's own share changes
      setSharedUsers(prev =>
        prev.map(u => u.uid !== userId ? u : u.viaProject ? { ...u, directPermission: newPermission } : { ...u, permission: newPermission })
      );
      onChanged?.();
      showFeedback('Permission updated', 'success');
    } else {
      showFeedback(result.error || 'Failed to update', 'error');
    }
  };

  if (!isOpen) return null;

  return (
    // The scrim keeps its own layer (100): above the top bar, Menu and the Nodes/Edit switch
    <div className="ns-modal-scrim" onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="ns-modal-sheet ns-modal-sheet-flush max-w-lg"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-3 pl-6 pr-5 pt-5 pb-4 border-b border-cream-300">
          <div className="min-w-0">
            <h2 id={titleId} className="ns-t-dialog">Share {noun}</h2>
            <p className="text-sm text-ink-soft truncate max-w-[300px]">{projectName}</p>
            {kind === 'project' && (
              <p className="ns-t-small mt-1" data-project-share-note>Everyone you add sees every workspace and folder in it, also ones you add later.</p>
            )}
          </div>
          <button onClick={onClose} className="ns-icon-btn" aria-label="Close">
            <X />
          </button>
        </div>

        {/* Invite form */}
        <form onSubmit={handleShare} className="px-6 py-4 border-b border-cream-300">
          <div className="flex gap-2">
            <div className="flex-1 relative">
              <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-faint pointer-events-none" />
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="Enter email address"
                className="ns-field pl-10"
                disabled={isSharing}
              />
            </div>
            <select
              value={permission}
              onChange={e => setPermission(e.target.value as SharePermission)}
              className="ns-field w-auto shrink-0"
              disabled={isSharing}
            >
              <option value="view">View</option>
              <option value="edit">Edit</option>
            </select>
            <button
              type="submit"
              disabled={isSharing || !email.trim()}
              className="ns-btn ns-btn-primary ns-btn-sm ns-btn-flat shrink-0"
            >
              {isSharing ? <Loader2 size={14} className="animate-spin" /> : <UserPlus size={14} />}
              Share
            </button>
          </div>
        </form>

        {/* Feedback */}
        {feedback && (
          <div
            className="ns-note mx-6 mt-3"
            data-tone={feedback.type === 'success' ? 'ok' : feedback.type === 'error' ? 'danger' : 'lavender'}
          >
            {feedback.type === 'success' ? <Check size={14} /> :
             feedback.type === 'error' ? <AlertCircle size={14} /> :
             <AlertCircle size={14} />}
            {feedback.message}
          </div>
        )}

        {/* Shared users list */}
        <div className="px-6 py-4 max-h-[320px] overflow-y-auto">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 size={20} className="animate-spin text-ink-faint" />
            </div>
          ) : (
            <>
              {/* Owner */}
              <div className="flex items-center justify-between gap-3 py-2.5">
                <div className="flex items-center gap-3 min-w-0">
                  <span className="ns-avatar">
                    <Crown size={14} />
                  </span>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-ink">You (Owner)</p>
                    <p className="ns-t-small">Full access</p>
                  </div>
                </div>
                <span data-tone="lavender" className="ns-badge ns-badge-tone">Owner</span>
              </div>

              {/* Shared users */}
              {sharedUsers.map(user => (
                <div key={user.uid} className="flex items-center justify-between gap-3 py-2.5 border-t border-cream-300">
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="ns-avatar">
                      {(user.name || user.email)[0]?.toUpperCase()}
                    </span>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-ink truncate">{user.name || user.email}</p>
                      <p className="ns-t-small truncate">{user.email}</p>
                      {user.viaProject && (
                        <p className="ns-t-small flex items-center gap-1 min-w-0" data-via-project={user.viaProject}>
                          <FolderKanban size={12} className="shrink-0" aria-hidden="true" />
                          <span className="truncate">{user.directPermission ? 'Also through' : 'Through'} the project {user.viaProjectName || ''}</span>
                        </p>
                      )}
                    </div>
                  </div>
                  {user.viaProject && !user.directPermission ? (
                    // Access through the project is changed in the project's own sharing
                    <span className="ns-t-meta flex items-center gap-1 shrink-0" title="Change it in the project's sharing">
                      {user.permission === 'edit' ? <Edit2 size={12} /> : <Eye size={12} />}
                      {user.permission === 'edit' ? 'Edit' : 'View'}
                    </span>
                  ) : (
                  <div className="flex items-center gap-2 shrink-0">
                    <select
                      value={user.directPermission ?? user.permission}
                      onChange={e => handlePermissionChange(user.uid, e.target.value as SharePermission)}
                      className="ns-field ns-field-sm w-auto"
                      aria-label={`Access for ${user.name || user.email}`}
                    >
                      <option value="view">View</option>
                      <option value="edit">Edit</option>
                    </select>
                    <button
                      onClick={() => handleRevoke(user.uid)}
                      className="ns-icon-btn ns-icon-btn-sm ns-icon-btn-danger"
                      title="Remove access"
                      aria-label="Remove access"
                    >
                      <Trash2 />
                    </button>
                  </div>
                  )}
                </div>
              ))}

              {/* Pending invitations */}
              {pendingInvitations.map(inv => (
                <div key={inv.id} className="flex items-center justify-between gap-3 py-2.5 border-t border-cream-300">
                  <div className="flex items-center gap-3 min-w-0">
                    <span data-tone="ink" className="ns-mark ns-mark-sm text-ink-soft">
                      <Clock />
                    </span>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-ink-soft truncate">{inv.email}</p>
                      <p className="text-xs text-warn-deep">{inv.inviteeUid ? 'Invited — waiting for them to accept' : 'Invited — pending signup'}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="ns-t-meta flex items-center gap-1">
                      {inv.permission === 'edit' ? <Edit2 size={12} /> : <Eye size={12} />}
                      {inv.permission === 'edit' ? 'Edit' : 'View'}
                    </span>
                    <button
                      onClick={() => handleCancelInvitation(inv.id)}
                      className="ns-icon-btn ns-icon-btn-sm ns-icon-btn-danger"
                      title="Cancel invitation"
                      aria-label="Cancel invitation"
                    >
                      <X />
                    </button>
                  </div>
                </div>
              ))}

              {sharedUsers.length === 0 && pendingInvitations.length === 0 && (
                <p className="text-sm text-ink-soft text-center py-6">
                  This {noun} hasn't been shared yet.
                </p>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};
